import React from 'react';
import type { PillarDef, PillarResult } from '../lib/pillarScoring';
import { iconForPillarLabel } from '../lib/statusIcons';
import { EXPECTED_SIGNALS } from '../lib/expectedSignals';

interface Props {
  def: PillarDef;
  result: PillarResult;
  onClose: () => void;
  periodLabel?: string; // e.g. "7-Day Daily Average" or "Yesterday"
}

export default function PillarModal({ def, result, onClose, periodLabel }: Props) {
  const hasScore = result.score != null;
  const activeRange = hasScore
    ? [...def.ranges].reverse().find(r => result.score! >= r.min)
    : null;
  const pct = def.maxInputs > 0 ? Math.round((result.inputCount / def.maxInputs) * 100) : 0;
  const confidenceLabel = pct >= 80 ? 'High' : pct >= 50 ? 'Moderate' : pct > 0 ? 'Low' : 'None';
  const confidenceColor = pct >= 80 ? '#00A896' : pct >= 50 ? '#F59E0B' : '#EF4444';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 space-y-4 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-arete-navy">{def.name}</h2>
            <p className="text-xs text-arete-muted mt-0.5">{def.description}</p>
            {periodLabel && (
              <span className="inline-block text-[10px] font-semibold text-arete-muted bg-gray-100 rounded-md px-2 py-0.5 mt-1.5 uppercase tracking-wide">
                {periodLabel}
              </span>
            )}
            <div className="flex items-center gap-2 mt-2">
              <span className="text-3xl font-bold" style={{ color: hasScore ? result.color : '#94A3B8' }}>
                {hasScore ? result.score : '—'}
              </span>
              {activeRange && (
                <span
                  className="text-sm font-medium px-2 py-0.5 rounded-full text-white"
                  style={{ backgroundColor: activeRange.color }}
                >
                  {activeRange.label}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-arete-muted hover:text-arete-navy text-xl font-bold mt-1">
            ×
          </button>
        </div>

        {/* Score Ranges */}
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-arete-muted uppercase tracking-wide">Score Ranges</p>
          {def.ranges.map(r => {
            const isActive = hasScore && result.score! >= r.min && result.score! <= r.max;
            return (
              <div key={r.label} className="flex items-center gap-2">
                <span className="w-4 text-center text-xs flex-shrink-0" style={{ color: r.color }}>{iconForPillarLabel(r.label) || '●'}</span>
                <span className="text-xs text-arete-muted w-16">{r.min}–{r.max}</span>
                <span className={`text-xs font-medium ${isActive ? 'text-arete-navy font-bold' : 'text-arete-muted'}`}>
                  {r.label} {isActive ? '← you' : ''}
                </span>
              </div>
            );
          })}
        </div>

        <hr className="border-gray-100" />

        {/* Contributing Signals */}
        {result.inputs.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-arete-muted uppercase tracking-wide">Your Contributing Signals</p>
            <div className="space-y-1.5">
              {result.inputs.map((input, i) => (
                <div key={i} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-xs text-arete-navy font-medium">{input.label}</span>
                    <span className="text-xs text-arete-muted ml-1.5">{input.rawValue}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <div className="w-12 h-1.5 rounded-full bg-gray-200">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.min(100, input.normalized)}%`,
                          backgroundColor: input.normalized >= 80 ? '#00A896' : input.normalized >= 60 ? '#22C55E' : input.normalized >= 40 ? '#F59E0B' : '#EF4444',
                        }}
                      />
                    </div>
                    <span className="text-[10px] text-arete-muted w-6 text-right">{Math.round(input.normalized)}</span>
                  </div>
                </div>
              ))}
            </div>
            {!hasScore && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                Not enough data to calculate a reliable score. Complete a check-in or sync your wearable to unlock this pillar.
              </p>
            )}
          </div>
        )}

        {result.inputs.length === 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-arete-muted uppercase tracking-wide">Contributing Signals</p>
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              No data available yet. Complete a check-in or sync your wearable to populate this pillar.
            </p>
          </div>
        )}

        {/* Data Confidence */}
        {def.maxInputs > 1 && (() => {
          const expected = EXPECTED_SIGNALS[def.key] || [];
          const presentLabels = new Set(result.inputs.map(i => i.label));
          const missing = expected.filter(s => !presentLabels.has(s.label));

          return (
            <div className="space-y-2 py-1">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-arete-muted uppercase tracking-wide">Data Confidence</span>
                  <span className="text-[10px] font-semibold" style={{ color: confidenceColor }}>
                    {confidenceLabel} — {result.inputCount} of {def.maxInputs} signals
                  </span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-gray-200">
                  <div
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: confidenceColor }}
                  />
                </div>
              </div>

              {/* Missing signals — show what's needed for higher confidence */}
              {missing.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
                  <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 mb-1">
                    Missing {missing.length} signal{missing.length > 1 ? 's' : ''}
                  </p>
                  {missing.map(s => (
                    <div key={s.label} className="flex items-start gap-1.5 mb-0.5">
                      <span className="text-[9px] text-amber-500 mt-0.5">○</span>
                      <div>
                        <span className="text-[10px] font-medium text-amber-800 dark:text-amber-300">{s.label}</span>
                        <span className="text-[10px] text-amber-600 dark:text-amber-400 ml-1">— {s.howToAdd}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        <hr className="border-gray-100" />

        {/* Data Source */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-arete-muted uppercase tracking-wide">Data Source</p>
          <p className="text-sm text-arete-muted">{def.source}</p>
        </div>

        {/* How Calculated */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-arete-muted uppercase tracking-wide">How It's Calculated</p>
          <p className="text-sm text-arete-muted">{def.how}</p>
        </div>

        {/* What It Means */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-arete-muted uppercase tracking-wide">What It Means</p>
          <p className="text-sm text-arete-muted">{def.meaning}</p>
        </div>
      </div>
    </div>
  );
}
